import { Observable } from 'rxjs';

import { Content, ContentDatabaseModifier, ContentVisibility, type FolderType } from '@efficiently/models';

/**
 * Parameters for a multi-file content upload. All target fields are optional —
 * callers pass only what the downstream GraphQL mutation actually needs.
 */
export interface UploadContentsArgs {
	files: File[];
	projectId?: string;
	projectOrgId?: string;
	projectOrgCode?: string;
	folderId?: string;
	folderParent?: string;
	parentId?: string;
	parentType?: string;
	documentType?: string;
	visibility?: ContentVisibility;
	modifier?: ContentDatabaseModifier;
}

/**
 * Public API for content upload. Used as a DI token so consumers stay
 * decoupled from the concrete store implementation.
 */
export abstract class ContentUploadStore {
	/**
	 * One-shot event stream of successfully uploaded contents. The underlying
	 * state is cleared on emission, so late subscribers (e.g. a component
	 * re-created after a tab switch) do not replay already-consumed events.
	 */
	public abstract readonly uploadCompleted$: Observable<Content[]>;

	/** Last upload error, `null` when there is none. */
	public abstract readonly error$: Observable<string | null>;

	/**
	 * Uploads a single file, resolving the target folder by `folderType`.
	 *
	 * `visibility` defaults to `Private` downstream; pass `Public` for branding
	 * assets (logo, welcome image) so the resulting CDN URL is served from the
	 * public prefix without signed cookies — required for anonymous (Branded
	 * Login) and portal-user (Client Portal) viewers.
	 */
	public abstract uploadContent(file: File, folderType: FolderType, visibility?: ContentVisibility): void;

	/** Uploads a batch of files with explicit target parameters. */
	public abstract uploadContents(args: UploadContentsArgs): void;
}
